import { DateFormatPattern } from '../api/types';

type Segment = 'D' | 'M' | 'Y';

function segmentsOf(pattern: DateFormatPattern): { order: Segment[]; separator: string } {
  const separator = pattern.includes('/') ? '/' : '-';
  const order = pattern.split(separator).map((part) => part[0] as Segment);
  return { order, separator };
}

/** Formate une date ISO (AAAA-MM-JJ, ou un datetime ISO complet dont seule la partie date est gardée) selon le format préféré de l'utilisateur — pour AFFICHAGE seulement, jamais pour l'échange avec le serveur (qui reste toujours ISO). */
export function formatDate(iso: string | null | undefined, pattern: DateFormatPattern): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (!y || !m || !d) return '';
  const { order, separator } = segmentsOf(pattern);
  return order.map((seg) => (seg === 'D' ? d : seg === 'M' ? m : y)).join(separator);
}

/**
 * Normalise une date saisie librement selon le format préféré de
 * l'utilisateur : accepte toujours l'ISO complet (AAAA-MM-JJ) tel quel, le
 * format préféré au complet (jour/mois/année dans l'ordre choisi, '-' ou '/'
 * accepté comme séparateur peu importe celui du format), et — demande
 * explicite — les deux premiers segments (jour+mois, ou mois+jour selon le
 * format) SANS année, complétée par l'année en cours. Renvoie null si non
 * reconnu (l'appelant décide : garder tel quel pour laisser la validation
 * serveur le signaler, comme dans le calendrier, ou refuser la saisie
 * immédiatement, comme dans le tableau de tâches).
 */
export function normalizeDateToken(raw: string, pattern: DateFormatPattern = 'DD-MM-YYYY'): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const { order } = segmentsOf(pattern);

  const full = s.match(/^(\d{1,4})[/-](\d{1,4})[/-](\d{1,4})$/);
  if (full) {
    const values = [full[1], full[2], full[3]];
    const bysegment: Partial<Record<Segment, string>> = {};
    order.forEach((seg, i) => (bysegment[seg] = values[i]));
    if (bysegment.D && bysegment.M && bysegment.Y) {
      const year = bysegment.Y.length === 2 ? `20${bysegment.Y}` : bysegment.Y.padStart(4, '0');
      return `${year}-${bysegment.M.padStart(2, '0')}-${bysegment.D.padStart(2, '0')}`;
    }
  }

  const partial = s.match(/^(\d{1,2})[/-](\d{1,2})$/);
  if (partial) {
    const dayMonthOrder = order.filter((seg): seg is 'D' | 'M' => seg !== 'Y');
    const values = [partial[1], partial[2]];
    const bysegment: Partial<Record<Segment, string>> = {};
    dayMonthOrder.forEach((seg, i) => (bysegment[seg] = values[i]));
    if (bysegment.D && bysegment.M) {
      return `${new Date().getFullYear()}-${bysegment.M.padStart(2, '0')}-${bysegment.D.padStart(2, '0')}`;
    }
  }

  return null;
}
